import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Box, Grid, Paper, alpha, useTheme } from '@mui/material';

import { createDefaultOffersHubState } from '~/utils/societePayloadBuilder/offersHub/defaults';
import { hasOffersHubProgress } from '~/utils/societePayloadBuilder/offersHub/hasProgress';
import { parseOffersHubPayload } from '~/utils/societePayloadBuilder/offersHub/parsePayload';
import type { OffersHubPayloadBuilderState } from '~/utils/societePayloadBuilder/offersHub/types';
import { validateOffersHubPayload } from '~/utils/societePayloadBuilder/offersHub/validatePayload';
import { parseJsonObject } from '~/utils/societePayloadBuilder/parseHelpers';

import OffersHubPayloadActions from './OffersHubPayloadActions';
import OffersHubPayloadBuilderForm from './OffersHubPayloadBuilderForm';
import OffersHubPayloadPreview from './OffersHubPayloadPreview';

const WORKSPACE_MAX_HEIGHT = 'calc(100vh - 300px)';

interface OffersHubPayloadBuilderPageProps {
  onProgressChange?: (hasProgress: boolean) => void;
}

export default function OffersHubPayloadBuilderPage({ onProgressChange }: OffersHubPayloadBuilderPageProps) {
  const theme = useTheme();
  const borderSoft = alpha(theme.palette.divider, 0.12);

  const [state, setState] = useState<OffersHubPayloadBuilderState>(() => createDefaultOffersHubState());
  const [loadJson, setLoadJson] = useState('');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [collapseResetKey, setCollapseResetKey] = useState(0);
  const [loadCollapseKey, setLoadCollapseKey] = useState(0);

  const validation = useMemo(() => validateOffersHubPayload(state), [state]);

  useEffect(() => {
    onProgressChange?.(hasOffersHubProgress(state, loadJson));
  }, [loadJson, onProgressChange, state]);

  const applyJson = useCallback((text: string) => {
    try {
      setState(parseOffersHubPayload(parseJsonObject(text)));
      setCollapseResetKey((value) => value + 1);
      setLoadCollapseKey((value) => value + 1);
      setLoadError(null);
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : 'Invalid JSON');
    }
  }, []);

  const handleLoadPayload = useCallback(() => {
    applyJson(loadJson);
  }, [applyJson, loadJson]);

  const handleUploadFile = useCallback(
    (file: File) => {
      const reader = new FileReader();
      reader.onload = () => {
        const text = typeof reader.result === 'string' ? reader.result : '';
        setLoadJson(text);
        applyJson(text);
      };
      reader.readAsText(file);
    },
    [applyJson],
  );

  const panelSx = {
    borderRadius: 2,
    border: `1px solid ${borderSoft}`,
    maxHeight: WORKSPACE_MAX_HEIGHT,
    overflow: 'auto',
    boxSizing: 'border-box',
  } as const;

  return (
    <Box>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 5.4 }}>
          <Paper elevation={0} sx={{ ...panelSx, p: 1.75 }}>
            <OffersHubPayloadBuilderForm
              collapseResetKey={collapseResetKey}
              state={state}
              onChange={setState}
              validation={validation}
              loadJson={loadJson}
              onLoadJsonChange={setLoadJson}
              loadError={loadError}
              loadCollapseKey={loadCollapseKey}
              onLoadPayload={handleLoadPayload}
              onClearLoad={() => {
                setLoadJson('');
                setLoadError(null);
              }}
              onUploadFile={handleUploadFile}
            />
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 6.6 }}>
          <Box
            sx={{
              mb: 1.5,
              p: 1.5,
              borderRadius: 2,
              border: `1px solid ${borderSoft}`,
              bgcolor: 'background.paper',
              display: 'flex',
              justifyContent: 'flex-end',
            }}
          >
            <OffersHubPayloadActions state={state} isValid={validation.isValid} fullWidth />
          </Box>
          <OffersHubPayloadPreview state={state} borderSoft={borderSoft} maxHeight={WORKSPACE_MAX_HEIGHT} />
        </Grid>
      </Grid>
    </Box>
  );
}
